
import Vue from 'vue';
import Player from '@media/components/base/Player.vue';
import config from '@media/config/media.config';
import { getAudio } from '@media/services/getAudio';
import { AudioItem } from '@media/types/getAudio';
import { PortalPlayer } from '@media/services/player';
import jwplayer from '@media/types/JwPlayer';
import getDurationString from '@media/utils/getDurationString';
import { Data as Article } from '@root/modules/article/types/article';
import { stripTags } from '@root/common/filters/htmlFormater';
import { CustomerData } from '@root/modules/customer/types/customer';
import isMobileView from '@root/common/utils/isMobileView';

interface Props {
  data: {
    attrs: AudioItem;
    src: string;
  };
  type: string;
  ads: Record<string, unknown>;
  article: Article;
}

interface AudioMeta {
  title: string | null;
  credit: string;
  pic: string | null;
}

interface Data {
  activeEpisodeId: string;
  trackChanged: boolean;
  player: jwplayer.JWPlayer | null;
  containerId: string;
  isPlayed: boolean;
  audioData: AudioItem | null;
  audioMeta: AudioMeta;
  playlist: AudioItem[];
  elapsedTime: number | string;
  isMobileView: boolean;
}

interface Computed {
  customerToken: string | null;
  hasChannelAccess: boolean;
  playlistData: CustomerData[];
  title: string;
  toggleIcon: string;
  toggleClass: string;
}

interface Methods {
  handleTrackChange: (id: string) => void;
  mountPlayer: (settings: Record<string, unknown>, mediaData: AudioItem, startPlaying?: boolean) => void;
  setPlayerSettings: (mediaData: AudioItem) => Record<string, unknown>;
  getAudioMeta: (mediaData: AudioItem) => AudioMeta;
  toggle: () => void;
  playerLayout: () => void;
  initializePlayer: (id: string) => void;
  initializeTtsPlayer: (src: string) => void;
  navigateToFone: () => void;
  addToPlaylist: () => void;
  removeFromPlaylist: () => void;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    Player,
  },
  props: {
    type: {
      type: String,
      required: true,
    },
    data: {
      type: Object,
      required: true,
    },
    ads: {
      type: Object,
      required: false,
      default: null,
    },
    article: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      activeEpisodeId: '',
      containerId: `media-widget-${this.type}-${this.article?.id || this.data.attrs?.id}`,
      isPlayed: false,
      audioData: null,
      trackChanged: false,
      audioMeta: {} as AudioMeta,
      playlist: [],
      player: null,
      optionsType: 'rate',
      elapsedTime: '00:00',
      isMobileView: false,
    };
  },
  computed: {
    customerToken() {
      return this.$store.state.piano.token;
    },
    hasChannelAccess() {
      return this.$store.state.piano.access.channelAccess;
    },
    playlistData() {
      return this.$store.state.podcast.playlistData;
    },
    title() {
      return this.article ? stripTags(this.article.content.title.html) : this.audioMeta?.title || '';
    },
    toggleIcon() {
      return this.isPlayed ? 'toggle-pause' : 'toggle-play';
    },
    toggleClass() {
      return this.isPlayed ? 'audio-player__control--pause' : 'audio-player__control--play';
    },
  },
  watch: {
    async customerToken() {
      if (this.customerToken) {
        await this.$store.dispatch('podcast/setCustomerToken', this.customerToken);
        await this.$store.dispatch('podcast/setCustomerPlaylist');
      }
    },
    data: {
      deep: true,
      handler(value, old) {
        if (value.src && value.src !== old.src) {
          this.initializeTtsPlayer(value.src);
        }
      },
    },
  },
  methods: {
    handleTrackChange(id) {
      this.trackChanged = true;
      this.isPlayed = false;
      let index;
      this.playlist.forEach((item, i) => {
        if (item.id === id) {
          index = i;
        }
      });
      if (typeof index === 'number' && index >= 0) {
        const item = this.playlist[index];
        if (item) {
          this.activeEpisodeId = item.id;
          this.audioMeta.title = item?.metadata.title || '';

          const settings = this.setPlayerSettings(item);
          this.mountPlayer(settings, item, true);
          this.isPlayed = true;
        }
      }
    },
    getAudioMeta(mediaData) {
      let credit = this.data?.attrs.metadata?.credit || mediaData?.metadata.credit || '';
      const title = this.data?.attrs.metadata?.title || mediaData?.metadata.title || '';
      const pic = mediaData.metadata.audioPreviewImage?.id ? `${config.api.mediaApiUrl}/${mediaData.metadata.audioPreviewImage?.id}.jpg?w=172&h=172` : null;
      if (this.playlist.length > 0) {
        credit = mediaData?.podcasts?.items[0]?.metadata.title || credit;
      }

      return { credit, title, pic };
    },
    setPlayerSettings(mediaData) {
      const file = mediaData.audioData?.audioPlaylistLocation;

      return {
        preload: 'auto',
        width: '100%',
        height: 1,
        volume: 50,
        mute: false,
        title: this.audioMeta.title,
        file,
        playbackRateControls: false,
        intl: {
          en: {
            advertising: {
              cuetext: '',
            },
          },
        },
      };
    },
    mountPlayer(settings, mediaData, startPlaying?: boolean) {
      const player = new PortalPlayer(this.containerId, settings, mediaData);
      if (player instanceof PortalPlayer) {
        player.on('player-ready', (jwplayer) => {
          player.setLayout(this.article ? 'AUDIO' : this.data.attrs.type);
          this.player = jwplayer;
          this.playerLayout();
        });

        if (startPlaying) {
          this.player?.play();
        }
      }
    },
    playerLayout() {
      const playerContainer = this.player?.getContainer() as HTMLElement;
      const buttonContainer = playerContainer?.querySelector('.jw-controlbar');
      const elapsedTime = buttonContainer?.querySelector('.jw-text-elapsed') as HTMLElement;
      elapsedTime.innerText = '00:00';
      const durationTime = buttonContainer?.querySelector('.jw-text-duration') as HTMLElement;
      durationTime.innerText = '00:001';
      const sliderBar = this.$refs.controlBar as HTMLElement;

      if (this.trackChanged) {
        const currentDuration = sliderBar.querySelector('.jw-text-duration:last-child');
        if (currentDuration) {
          currentDuration.remove();
        }
        const currentElapsedTime = sliderBar.querySelector('.jw-text-elapsed:first-child');
        if (currentElapsedTime) {
          currentElapsedTime.remove();
        }

        const currentSlider = sliderBar.querySelector('.jw-slider-time:first-child');
        if (currentSlider) {
          currentSlider.remove();
        }
      }

      sliderBar.appendChild(durationTime);

      // Append duration string to UI container
      const playerUIContainer = document.querySelector(`.audio-player--${this.article?.id || this.data.attrs?.id}`) as HTMLElement;
      const durationStringContainer = playerUIContainer?.querySelector('.audio-player__duration') as HTMLElement;
      const duration = this.player?.getDuration();
      if (duration) {
        const durationString = getDurationString(duration);
        if (durationString) {
          durationStringContainer.innerHTML = durationString;
        }
      } else {
        durationStringContainer.style.display = 'none';
      }

      playerContainer.style.height = '0px';
      this.elapsedTime = elapsedTime.innerHTML;

      this.player?.on('seeked', () => {
        this.player?.play();
        this.isPlayed = true;
      });

      this.player?.on('adPlay', (data) => {
        if (data.oldstate === 'buffering') {
          const slider = buttonContainer?.querySelector('.jw-slider-time') as HTMLElement;
          slider.classList.add('jw-slider-time--ads-slider');
          sliderBar.prepend(slider);
        }
        this.isPlayed = true;
      });

      this.player?.on('firstFrame', () => {
        const slider = buttonContainer?.querySelector('.jw-slider-time') as HTMLElement;
        slider.classList.remove('jw-slider-time--ads-slider');
        sliderBar.prepend(slider);
      });
      this.player?.on('adTime', () => {
        this.elapsedTime = elapsedTime.innerHTML;
      });
      this.player?.on('time', () => {
        this.elapsedTime = elapsedTime.innerHTML;
      });
      this.player?.on('adComplete', () => {
        this.elapsedTime = elapsedTime.innerHTML;
      });

      this.player?.on('complete', () => {
        this.isPlayed = false;

        if (this.article && !this.hasChannelAccess) {
          this.$emit('show-teaser-complete-modal');
        }
      });
    },
    toggle() {
      this.isPlayed ? this.player?.pause() : this.player?.play();
      this.isPlayed = !this.isPlayed;
    },
    async initializePlayer(id: string) {
      const res = await getAudio({ id });

      if (!res?.audio.items[0]) {
        return;
      }

      this.audioData = res.audio.items[0];
      const podcastEpisodes = this.audioData.podcasts?.items[0]?.episodes.items || [];

      // If podcasts exist and more than one episode
      if (podcastEpisodes.length > 0) {
        this.activeEpisodeId = this.audioData.id;
        this.playlist = podcastEpisodes;
      }

      this.audioMeta = this.getAudioMeta(this.audioData);
      const settings = this.setPlayerSettings(this.audioData);
      this.mountPlayer(settings, this.audioData);
    },
    initializeTtsPlayer(src: string) {
      const metadata = {
        title: this.title,
        credit: null,
        description: this.article.content?.lead?.text,
        authors: this.article.authors?.items?.map((author) => author.name),
        tags: this.article.tags?.items?.map((tag) => tag.name),
        adsDisabled: true,
        audioPreviewImage: {
          id: this.article.metaImage?.id || null,
        },
      };

      const data = { id: String(this.article.id), type: 'AUDIO' as const, metadata, audioData: { audioPlaylistLocation: src } };
      const settings = this.setPlayerSettings(data);

      this.mountPlayer(settings, data);
    },
    navigateToFone() {
      location.href = 'https://www.delfi.lt/fone';
    },
    async addToPlaylist() {
      if (!this.customerToken) {
        this.$store.dispatch('piano/showLoginModal', { screen: 'login', callback: () => setTimeout(() => this.addToPlaylist(), 500) });

        return;
      }

      const episode = this.article;
      if (!episode) {
        return;
      }

      await this.$store.dispatch('podcast/setCustomerToken', this.customerToken);
      await this.$store.dispatch('podcast/addToPlaylist', episode.id);

      this.$alertHandler.addAlert({
        type: 'success',
        title: this.$t('podcast.alerts.addToPlaylist') as string,
        link: { href: 'https://www.delfi.lt/fone/mano-grojarastis', text: 'Mano grojaraštis' },
        fadeTrigger: 5000,
        closeButton: false,
        options: {
          key: 'podcast-add-to-playlist',
        },
      });
    },
    async removeFromPlaylist() {
      const episode = this.article;
      if (!episode) {
        return;
      }

      await this.$store.dispatch('podcast/setCustomerToken', this.customerToken);
      await this.$store.dispatch('podcast/removeFromPlaylist', episode.id);

      this.$alertHandler.addAlert({
        type: 'info',
        title: this.$t('podcast.alerts.removeFromPlaylist') as string,
        fadeTrigger: 5000,
        closeButton: false,
        options: {
          key: 'podcast-remove-from-playlist',
        },
      });
    },
  },
  async mounted() {
    this.isMobileView = isMobileView();

    if (this.data.attrs?.id) {
      await this.initializePlayer(this.data.attrs.id);
    } else if (this.data.src) {
      if (this.customerToken) {
        await this.$store.dispatch('podcast/setCustomerToken', this.customerToken);
        await this.$store.dispatch('podcast/setCustomerPlaylist');
      }

      await this.initializeTtsPlayer(this.data.src);
    } else {
      return false;
    }
  },
});
